import * as React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface PhoneTab {
  id: string;
  label: string;
  Icon: React.ElementType;
  active: boolean;
  onActivate: () => void;
}

/** Height of the bar, shared with the shell (content padding) and the theme picker (its offset). */
export const PHONE_TAB_BAR_HEIGHT = 72;

/**
 * The phone bottom tab bar (handoff step 8, from Bennie-Holiday v2). Each tab is a native button;
 * the active one carries `aria-current="page"`.
 */
export function PhoneTabBar(props: { tabs: readonly PhoneTab[] }) {
  const { t } = useTranslation();
  return (
    <Box
      component="nav"
      aria-label={t('nav:rail.navLabel')}
      data-testid="phone-tab-bar"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.custom.zIndex.header,
        height: PHONE_TAB_BAR_HEIGHT,
        boxSizing: 'border-box',
        pb: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        background: (theme) => (theme.palette.mode === 'dark' ? theme.palette.background.paper : 'linear-gradient(135deg,#fff,#fafbfd)'),
        borderTop: (theme) => `1px solid ${theme.custom.colors.border.light}`,
        boxShadow: (theme) => (theme.palette.mode === 'dark' ? '0 -4px 16px rgba(0,0,0,.4)' : '0 -4px 16px rgba(0,30,70,.08)'),
      }}
    >
      {props.tabs.map(({ id, label, Icon, active, onActivate }) => (
        <Box
          key={id}
          component="button"
          type="button"
          data-testid={`phone-tab-${id}`}
          aria-current={active ? 'page' : undefined}
          onClick={onActivate}
          sx={{
            all: 'unset',
            boxSizing: 'border-box',
            minWidth: 44,
            flex: '1 1 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
            cursor: 'pointer',
            color: (theme) => (active ? theme.custom.colors.primary[500] : theme.custom.colors.text.tertiary),
            '&:focus-visible': { outline: (theme) => `2px solid ${theme.custom.colors.primary[500]}`, outlineOffset: '2px', borderRadius: '8px' },
          }}
        >
          <Box
            component="span"
            sx={{
              width: 60,
              maxWidth: '100%',
              height: 30,
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: (theme) => (active ? theme.custom.colors.primary.tint : 'transparent'),
              '& svg': { fontSize: 22 },
            }}
          >
            <Icon />
          </Box>
          <Box component="span" sx={{ fontSize: '11.5px', fontWeight: active ? 700 : 500, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            {label}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
